import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import AvatarImage from "../common/AvatarImage";

export type AvatarItem = {
  id: string;
  label: string;
  src: string;
  tags?: string[];
  filename?: string;
};

type Props = {
  open: boolean;
  title?: string;
  avatars?: AvatarItem[];
  selectedId?: string;
  onSelect: (avatar: AvatarItem) => void;
  onClose: () => void;
  onClear?: () => void;
};

function stableAvatarId(a: AvatarItem): string {
  return String(a.id || a.filename || a.src || "");
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
const LIST_ENDPOINT = "/public/avatars";

const THUMB_H = 200;
const IMG_MAX_PCT = "82%";

function normalizeAvatarUrl(raw: string, fallbackId?: string) {
  const v = String(raw || fallbackId || "").trim();
  if (!v) return "";

  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  if (v.startsWith("/")) return `${API_BASE_URL}${v}`;
  return `${API_BASE_URL}/public/avatars/${encodeURIComponent(v)}`;
}

function cacheBust(url: string) {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${Date.now()}`;
}

function unwrapAvatars(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.avatars)) return data.avatars;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

function buildLocalFallbackAvatars(): AvatarItem[] {
  const items: AvatarItem[] = [];
  for (let i = 1; i <= 17; i++) {
    const n = String(i).padStart(2, "0");
    items.push({
      id: `avatar_${n}`,
      label: `Avatar ${n}`,
      src: `/avatar/avatar_${n}.png`,
      filename: `avatar_${n}.png`,
      tags: ["fallback"],
    });
  }
  return items;
}

export default function AvatarGalleryModal({
  open,
  title = "Choose an avatar",
  avatars = [],
  selectedId = "",
  onSelect,
  onClose,
  onClear,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [serverAvatars, setServerAvatars] = useState<AvatarItem[]>([]);
  const [q, setQ] = useState("");

  const builtInFallback = useMemo(() => buildLocalFallbackAvatars(), []);

  async function loadFromServer() {
    setLoading(true);
    setErr("");

    try {
      const res = await api.get(`${LIST_ENDPOINT}?t=${Date.now()}`, {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Accept: "application/json",
        },
      });

      const items = unwrapAvatars(res.data);

      if (!Array.isArray(items) || items.length === 0) {
        setServerAvatars([]);
        setErr("Using fallback avatars. No avatars published yet.");
        return;
      }

      const normalized = items
        .map((a: any) => {
          const id = String(a?.id || a?.filename || a?.src || "").trim();
          if (!id) return null;

          const filename = a?.filename ? String(a.filename) : undefined;
          const label = String(a?.label || filename || a?.id || "Avatar").trim();
          const srcRaw = String(a?.src || a?.url || a?.path || "").trim();
          const src = cacheBust(normalizeAvatarUrl(srcRaw, id));
          if (!src) return null;

          return {
            id,
            filename,
            label,
            src,
            tags: Array.isArray(a?.tags) ? a.tags : undefined,
          } as AvatarItem;
        })
        .filter(Boolean) as AvatarItem[];

      setServerAvatars(normalized);
      setErr("");
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "Failed loading avatars";
      setErr(`Using fallback avatars. ${String(detail)}`);
      setServerAvatars([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) loadFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const effectiveAvatars = serverAvatars.length
    ? serverAvatars
    : avatars.length
    ? avatars
    : builtInFallback;

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return effectiveAvatars;
    return effectiveAvatars.filter((a) => {
      const hay = `${a.label || ""} ${a.id || ""} ${a.filename || ""} ${(a.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(qq);
    });
  }, [effectiveAvatars, q]);

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.topRow}>
          <div style={styles.title}>{title}</div>
          <button style={styles.btn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={styles.controls}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." style={styles.input} />
          <button style={styles.btn} onClick={loadFromServer} disabled={loading}>
            Refresh
          </button>
          {onClear ? (
            <button style={styles.btn} onClick={onClear}>
              Clear
            </button>
          ) : null}
        </div>

        {loading ? <div style={styles.msg}>Loading…</div> : null}
        {err ? <div style={{ ...styles.msg, color: "#b00020" }}>{err}</div> : null}

        <div style={styles.listScroll}>
          <div style={styles.grid}>
            {filtered.map((a) => {
              const sid = stableAvatarId(a);
              const label = String(a.label || a.filename || a.id || "Avatar");
              const src =
                a.src.startsWith("http://") ||
                a.src.startsWith("https://") ||
                a.src.startsWith("/")
                  ? a.src
                  : `/${a.src}`;
              const isSelected = sid === String(selectedId || "");

              return (
                <button
                  key={sid}
                  type="button"
                  style={{ ...styles.card, ...(isSelected ? styles.cardSelected : {}) }}
                  onClick={() => onSelect({ ...a, src })}
                  title={label}
                >
                  <AvatarImage
                    src={src}
                    alt={label}
                    width="100%"
                    height={THUMB_H}
                    fit="contain"
                    radius={14}
                    background="#f8fbff"
                    border="1px solid rgba(0,0,0,0.06)"
                    style={{ padding: 10, boxSizing: "border-box" }}
                    imgStyle={{
                      width: "auto",
                      height: "auto",
                      maxWidth: IMG_MAX_PCT,
                      maxHeight: IMG_MAX_PCT,
                    }}
                    fallback={<span style={{ fontWeight: 900, opacity: 0.8 }}>{label.slice(0, 1).toUpperCase()}</span>}
                  />

                  <div style={styles.label}>{label}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={styles.footer}>
          Showing {filtered.length} of {effectiveAvatars.length}
          {serverAvatars.length ? " (from server)" : " (fallback)"}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 9999,
    overflowY: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: "max(16px, env(safe-area-inset-top))",
    paddingBottom: "max(16px, env(safe-area-inset-bottom))",
  },
  modal: {
    width: "min(1400px, 98vw)",
    height: "min(900px, 92vh)",
    marginTop: 16,
    display: "flex",
    flexDirection: "column",
    background: "white",
    borderRadius: 14,
    padding: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  title: { fontWeight: 900, fontSize: 16 },
  controls: {
    display: "flex",
    gap: 10,
    marginTop: 12,
    marginBottom: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  input: {
    flex: 1,
    minWidth: 320,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ddd",
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 800,
  },
  msg: { padding: "8px 0", fontWeight: 800 },
  listScroll: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    paddingRight: 4,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    gap: 14,
    paddingBottom: 6,
  },
  card: {
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 16,
    padding: 12,
    background: "#fff",
    cursor: "pointer",
    textAlign: "center",
  },
  cardSelected: { outline: "3px solid rgba(36,198,220,0.85)" },
  label: {
    marginTop: 8,
    fontSize: 13,
    opacity: 0.85,
    fontWeight: 800,
  },
  footer: {
    marginTop: 10,
    fontSize: 12,
    opacity: 0.7,
    fontWeight: 700,
  },
};